import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withTheme } from 'styled-components'
import { ErrorBoundary } from '@sainsburys-tech/bolt'
import { useCookieConsent, useInterval } from '@sainsburys-tech/boltui-hooks'
import { canUseDom } from '@sainsburys-tech/boltui-utils'

const DFP001 = (props) => {
  const { channelId, taxonomy, position, areaId, theme } = props

  const [isScriptInjected, setIsScriptInjected] = useState(false)
  const [isApiReady, setIsApiReady] = useState(false)
  const [slotFulfilled, setSlotFulfilled] = useState(false)
  const [containerUniqueId, setContainerUniqueId] = useState('')

  const hasUserConsent = useCookieConsent('marketing')

  const bannerPlacements = ['top', 'bottom']

  const injectScript = () => {
    const scriptTag = document.createElement('script')
    scriptTag.async = true
    scriptTag.defer = true
    scriptTag.setAttribute('data-name', 'google-dfp')
    scriptTag.src = `//www.googletagservices.com/tag/js/gpt.js`
    document.head.appendChild(scriptTag)
    setIsScriptInjected(true)
  }

  const normalizeTaxonomy = () => {
    return taxonomy.split('-').join('')
  }

  const generateUniqueId = () => {
    return `dfp-${Math.random().toString(36).split('.').join('')}-${Math.random().toString(36).substr(2, 5)}`
  }

  const getNormalizedSlotNameByPosition = () => {
    const normalizedPositionName = position.toLowerCase()
    if (!bannerPlacements.includes(normalizedPositionName)) {
      return ''
    }
    return normalizedPositionName
  }

  const hostname = {
    argos: 'argos.co.uk',
    habitat: 'habitat.co.uk'
  }[theme.channel || 'argos']

  const getSlot = () => {
    return `/${channelId}/${hostname}/${normalizeTaxonomy(taxonomy)}/${getNormalizedSlotNameByPosition(position)}`
  }

  const useDfpSlot = ({ path, size, id }) => {
    const googletag = window.googletag || {}
    googletag.cmd = googletag.cmd || []

    googletag.cmd.push(() => {
      googletag.pubads().enableSingleRequest()
      googletag.pubads().setCentering(true)
      googletag.pubads().collapseEmptyDivs()
      googletag.defineSlot(path, size, id).addService(googletag.pubads())
      googletag.pubads().addEventListener('slotRenderEnded', (event) => {
        if (event.slot.getSlotElementId() === id) {
          setSlotFulfilled(!event.isEmpty)
        }
      })
      googletag.enableServices()
      googletag.display(id)
    })
  }

  // Inject script
  useEffect(() => {
    if (hasUserConsent && !isScriptInjected) injectScript()
    setContainerUniqueId(generateUniqueId())
  }, [hasUserConsent, isScriptInjected])

  // Poll for API-ready
  useInterval(
    () => {
      setIsApiReady(canUseDom() ? window.googletag && window.googletag.apiReady : false)
    },
    250,
    isApiReady
  )

  useEffect(() => {
    if (isApiReady) {
      useDfpSlot({
        path: getSlot(channelId, taxonomy, position),
        size: ['fluid', [870, 160], [728, 90], [345, 140]],
        id: containerUniqueId
      })
    }

    return () => isApiReady && window.googletag.destroySlots()
  }, [isApiReady, taxonomy])

  return (
    hasUserConsent && (
      <ErrorBoundary key={containerUniqueId}>
        <div
          id={containerUniqueId}
          data-area-id={areaId}
          data-slot-fulfilled={slotFulfilled}
          className='dfp-banner-row xs-stack md-row dfp001'
          style={{ marginBottom: '15px' }}
        />
      </ErrorBoundary>
    )
  )
}

DFP001.propTypes = {
  areaId: PropTypes.string.isRequired,
  channelId: PropTypes.number.isRequired,
  position: PropTypes.string.isRequired,
  taxonomy: PropTypes.string.isRequired
}

export default withTheme(DFP001)
